import { onSignedInUserChange, type SignedInUserChange } from "@/hooks/useAuth";
import { getAuthRedirectUrl } from "@/lib/edge-functions";
import { markPageLeaving } from "@/lib/page-leaving";

/** The parts of the page this touches, so tests can pass their own. */
export interface LeavingPage {
  location: Pick<Location, "replace" | "reload">;
  fetch: typeof fetch;
  document: { documentElement: { style: { visibility: string } } };
}

/**
 * Loads the page afresh whenever the signed-in user changes (#7026, Matt's option A).
 *
 * Work the previous user started can still be in flight: a mutation callback mid-await, an
 * onMutate about to send its request, a toast about to show. In a single-page app it would resume
 * against the next user's session, router and toasts, and JavaScript can't cancel it. Three
 * in-place isolations (#7022, #7023, #7027) each left another such path open. A fresh page load
 * ends all of it.
 *
 * Starting the load doesn't stop the current task, though, and code already queued can still run
 * before the unload (Codex on #7027). For that moment the page stops sending requests (anything
 * sent now would carry the next user's session) and hides itself, so leftover work can neither
 * reach the server nor show the next user anything. The per-user QueryClient
 * (src/lib/user-scoped-query-client.tsx) stays as defence in depth.
 *
 * - An explicit sign-out lands where it asked (`signOut({ redirectTo })`, "/auth" by default).
 * - A session that ended some other way (expired, or signed out in another tab) lands on the
 *   sign-in page with this page as the return path and the "Session expired" notice.
 * - A different user signing in (e.g. in another tab) reloads this page as them.
 */
export function destinationFor(change: SignedInUserChange): { replaceWith: string } | { reload: true } {
  if (change.nextUserId !== null) return { reload: true };
  return { replaceWith: change.signOutRedirect ?? getAuthRedirectUrl(undefined, "session-expired") };
}

export function reloadOnSignedInUserChange(page: LeavingPage = window): () => void {
  // One page load per change: a second event before the unload must not start another.
  let leaving = false;
  return onSignedInUserChange((change) => {
    if (leaving) return;
    leaving = true;
    // For code that would navigate another window after an await (see src/lib/page-leaving.ts).
    markPageLeaving();
    // A request that never settles: whatever awaits it stays parked until the page goes.
    page.fetch = () => new Promise<Response>(() => {});
    page.document.documentElement.style.visibility = "hidden";
    const destination = destinationFor(change);
    if ("reload" in destination) page.location.reload();
    else page.location.replace(destination.replaceWith);
  });
}
